/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useProjectScope } from '../../hooks/useProjectScope';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';

import FormInput from '../../components/forms/form-input';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import FormSelect from '../../components/forms/form-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';

import { departmentsService } from '../../services/departments.service';
import { ministriesService, type Ministry } from '../../services/ministries.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

// -----------------------------------------------------------------------------
// Zod Schema (mirrors backend DepartmentCreate/UpdateSchema)
// -----------------------------------------------------------------------------

// Department name: alphabets, spaces, hyphens, underscores, & and / only; min 2 alphabets.
const DEPARTMENT_NAME_PATTERN = /^[a-zA-Z\s\-_&/]+$/;

const departmentSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Must be ≤ 255 characters')
    .regex(DEPARTMENT_NAME_PATTERN, 'Only letters, spaces, hyphens, underscores, & and / are allowed')
    .refine((v) => (v.match(/[a-zA-Z]/g) || []).length >= 2, 'Must contain at least 2 letters'),
  // Department code: alphanumeric string — letters, digits, hyphens, underscores
  // (matches backend validate_code_string_field). No longer digits-only.
  departmentCd: z
    .string()
    .trim()
    .max(50, 'Must be ≤ 50 characters')
    .regex(/^[a-zA-Z0-9\-_]+$/, 'Only letters, digits, hyphens and underscores are allowed')
    .optional()
    .or(z.literal('')),
  ministryId: z.string().trim().optional().or(z.literal('')),
  appId: z.string().trim().optional().or(z.literal('')),
});

export type DepartmentFormData = z.infer<typeof departmentSchema>;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const DepartmentForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'departments',
    basePath: '/departments',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  // Fetch ministries for the ministryId dropdown (optional FK)
  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries', 'dropdown'],
    queryFn: ministriesService.getAllForDropdown,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema) as Resolver<DepartmentFormData>,
    defaultValues: {
      name: '',
      departmentCd: '',
      ministryId: '',
      appId: '',
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // A department's ministry must be in the same project or global (backend rule).
  // Scope the Ministry dropdown to the selected project (+ global) and tag each
  // option with its owning project.
  const { belongsToProject, projectTag } = useProjectScope(selectedAppId, accessibleProjects);

  const ministryOptions = ministries
    .filter((m: Ministry) => belongsToProject(m.appId))
    .map((m: Ministry) => ({ value: m._id, label: m.name, ...projectTag(m.appId) }));

  const handleMinistrySearch = async (searchTerm: string) => {
    try {
      const all = await ministriesService.getAllForDropdown();
      return all
        .filter((m: Ministry) => belongsToProject(m.appId))
        .filter((m: Ministry) => m.name.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((m: Ministry) => ({ value: m._id, label: m.name, ...projectTag(m.appId) }));
    } catch (error) {
      console.error('Error searching ministries:', error);
      return [];
    }
  };

  // Clear the selected ministry if it no longer belongs to the chosen project
  // (skipped on initial edit-load, which is already consistent).
  const selectedMinistryId = watch('ministryId');
  const prevAppIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;
    const m = ministries.find((x: Ministry) => x._id === selectedMinistryId);
    if (m?.appId && m.appId !== selectedAppId) setValue('ministryId', '');
  }, [selectedAppId, ministries, selectedMinistryId, setValue]);

  // Navigation blocker for unsaved changes
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Fetch existing entity when editing
  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['departments', id],
    queryFn: async () => await departmentsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show department name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? existing?.name : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        name: existing.name || '',
        departmentCd: existing.departmentCd || '',
        ministryId: existing.ministryId || '',
        appId: (existing as any).appId || '',
      });
    }
  }, [existing, isEditMode, reset]);

  // Mutations
  const createMut = useMutation({
    mutationFn: (payload: any) => departmentsService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Department created successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const errorMessage = extractErrorMessage(error, 'Failed to save Department');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: any) => departmentsService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['departments', id] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Department updated successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const errorMessage = extractErrorMessage(error, 'Failed to save Department');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = createMut.isPending || updateMut.isPending;

  // Submit
  const onSubmit = (data: DepartmentFormData) => {
    const payload: { name: string; departmentCd?: string; ministryId?: string; appId?: string } = {
      name: data.name,
    };
    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;

    // Only include optional fields when they have a value
    if (data.departmentCd && data.departmentCd.trim()) {
      payload.departmentCd = data.departmentCd.trim();
    }
    if (data.ministryId && data.ministryId.trim()) {
      payload.ministryId = data.ministryId.trim();
    }

    if (isEditMode) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  // Loading state
  if (fetchLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.DEPARTMENTS}>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-Department-Form">
        {/* Shared header — gradient tile + back button, matching list/dashboard screens */}
        <ScreenHeader
          icon="pi pi-sitemap"
          title={isEditMode ? 'Edit Department' : 'Create Department'}
          subtitle={isEditMode ? 'Update department information' : 'Add a new department'}
          onBack={() => handleNavigate('/departments')}
        />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Department Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                    placeholder="Enter department name"
                  />
                )}
              />

              {/* Department Code */}
              <Controller
                name="departmentCd"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="Department Code"
                    error={!!errors.departmentCd}
                    helperText={errors.departmentCd?.message}
                    placeholder="Enter code (e.g. FIN-01)"
                  />
                )}
              />

              {/* Ministry (optional FK) */}
              <Controller
                name="appId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Project"
                    options={projectOptions}
                    placeholder="Select project"
                    disabled={projectLocked || isEditMode}
                    error={!!errors.appId}
                    helperText={errors.appId?.message}
                  />
                )}
              />

              <Controller
                name="ministryId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSearchableSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Ministry"
                    onSearch={handleMinistrySearch}
                    initialOptions={ministryOptions}
                    selectedOption={ministryOptions.find((o) => o.value === value) || null}
                    placeholder="Search and select ministry"
                    error={!!errors.ministryId}
                    helperText={errors.ministryId?.message}
                  />
                )}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/departments')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      {/* Leave confirmation */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="DepartmentForm.Dialog.Leave"
      />
    </PermissionGuard>
  );
};

export default DepartmentForm;
